import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Link, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { isAdmin } from '../../services/authService';
import {
  listIdeas, triageIdea, deleteIdea, getIdeaOptions, PUBLIC_IDEA_PATH,
} from '../../services/workstreamIdeaService';

const STATUS_COLOR = {
  New: 'info', 'Under review': 'warning', Accepted: 'success', Planned: 'success',
  'In progress': 'primary', Done: 'default', Declined: 'error', Duplicate: 'default',
};
const PRIORITY_COLOR = { P1: 'error', P2: 'warning', P3: 'info', P4: 'default' };

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const publicUrl = () => `${window.location.origin}${PUBLIC_IDEA_PATH}`;

const Detail = ({ label, children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>{label}</Typography>
    <Typography variant="body2" component="div" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{children || '—'}</Typography>
  </Box>
);

const IdeaDialog = ({ idea, options, canEdit, onClose, onSaved, onDeleted }) => {
  const [draft, setDraft] = useState({
    status: idea.status, priority: idea.priority || '', targetQuarter: idea.targetQuarter || '',
    owner: idea.owner || '', linkedAdoItem: idea.linkedAdoItem || '', triageNotes: idea.triageNotes || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try { await fn(); } catch (err) { setError(err.message || 'Something went wrong'); } finally { setBusy(false); }
  };

  const save = () => run(async () => onSaved(await triageIdea(idea._id, draft)));
  const remove = () => {
    if (!window.confirm(`Delete "${idea.title}"? This cannot be undone.`)) return;
    run(async () => { await deleteIdea(idea._id); onDeleted(idea._id); });
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" scroll="paper">
      <DialogTitle sx={{ color: '#1D4434' }}>{idea.title}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1fr' }, gap: 3 }}>
          <Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip size="small" label={idea.category} />
              <Chip size="small" variant="outlined" label={idea.impactType} />
            </Box>
            <Detail label="Affected service / component">{idea.affectedService}</Detail>
            <Detail label="Problem">{idea.problem}</Detail>
            <Detail label="Proposed solution">{idea.proposedSolution}</Detail>
            <Detail label="Evidence links">
              {idea.evidenceLinks?.length ? idea.evidenceLinks.map((l) => (
                <Box key={l}><Link href={l} target="_blank" rel="noopener noreferrer">{l}</Link></Box>
              )) : null}
            </Detail>
            <Detail label="Related ADO work items">{idea.adoWorkItems?.join(', ')}</Detail>
            <Detail label="Submitted by">
              {[idea.submitterName, idea.submitterEmail].filter(Boolean).join(' · ')}
              {` · ${fmtDate(idea.createdAt)}`}
            </Detail>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#1D4434' }}>Triage</Typography>
            {!canEdit && <Alert severity="info" sx={{ mb: 2 }}>Only admins can triage suggestions.</Alert>}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField select size="small" label="Status" value={draft.status} onChange={set('status')} disabled={!canEdit}>
                {(options?.statuses || []).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Priority" value={draft.priority} onChange={set('priority')} disabled={!canEdit}>
                <MenuItem value="">Not set</MenuItem>
                {(options?.priorities || []).map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Target quarter / workstream" value={draft.targetQuarter} onChange={set('targetQuarter')} disabled={!canEdit} />
              <TextField size="small" label="Owner" value={draft.owner} onChange={set('owner')} disabled={!canEdit} />
              <TextField size="small" label="Linked ADO item" value={draft.linkedAdoItem} onChange={set('linkedAdoItem')} disabled={!canEdit} helperText="AB#12345" />
              <TextField size="small" label="Triage notes" value={draft.triageNotes} onChange={set('triageNotes')} disabled={!canEdit} multiline minRows={3} />
            </Box>
            {idea.statusChangedAt && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Status changed {fmtDate(idea.statusChangedAt)}</Typography>}
          </Box>
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {canEdit && <Button color="error" onClick={remove} disabled={busy} sx={{ mr: 'auto' }}>Delete</Button>}
        <Button onClick={onClose} disabled={busy}>{canEdit ? 'Cancel' : 'Close'}</Button>
        {canEdit && <Button variant="contained" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>}
      </DialogActions>
    </Dialog>
  );
};

/** Service Operations > Workstream Suggestions: every suggestion registered through the public form. */
const WorkstreamIdeasTab = () => {
  const [ideas, setIdeas] = useState([]);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [truncated, setTruncated] = useState(false);
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);
  const [filters, setFilters] = useState({ q: '', status: '', category: '', priority: '' });
  const canEdit = !!isAdmin();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [list, opts] = await Promise.all([listIdeas(), getIdeaOptions()]);
      setIdeas(list.data || []);
      setTruncated(!!list.truncated);
      setOptions(opts);
    } catch (err) {
      setError(err.message || 'Could not load suggestions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    return ideas.filter((i) => (
      (!filters.status || i.status === filters.status)
      && (!filters.category || i.category === filters.category)
      && (!filters.priority || (filters.priority === 'none' ? !i.priority : i.priority === filters.priority))
      && (!q || [i.title, i.problem, i.affectedService, i.owner, i.submitterEmail].some((v) => (v || '').toLowerCase().includes(q)))
    ));
  }, [ideas, filters]);

  const counts = useMemo(() => {
    const c = {};
    ideas.forEach((i) => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, [ideas]);

  const setFilter = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable; the Open button still works */ }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" sx={{ color: '#1D4434' }}>Workstream suggestions</Typography>
          <Typography variant="body2" color="text.secondary">
            Tech debt, reliability and stability suggestions registered through the public form, no sign-in needed.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyLink}>{copied ? 'Copied' : 'Copy public link'}</Button>
          <Button size="small" variant="contained" startIcon={<OpenInNewIcon />} href={PUBLIC_IDEA_PATH} target="_blank" rel="noopener noreferrer">Make a suggestion</Button>
        </Box>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {error && <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>{error}</Alert>}

      {!loading && !error && (
        <>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={`All ${ideas.length}`} onClick={() => setFilters((f) => ({ ...f, status: '' }))} color={filters.status ? 'default' : 'primary'} />
            {(options?.statuses || []).filter((s) => counts[s]).map((s) => (
              <Chip key={s} label={`${s} ${counts[s]}`} color={filters.status === s ? 'primary' : 'default'} variant={filters.status === s ? 'filled' : 'outlined'}
                onClick={() => setFilters((f) => ({ ...f, status: f.status === s ? '' : s }))} />
            ))}
          </Box>

          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <TextField size="small" label="Search" value={filters.q} onChange={setFilter('q')} sx={{ minWidth: 240, flex: 1 }} />
            <TextField select size="small" label="Category" value={filters.category} onChange={setFilter('category')} sx={{ minWidth: 180 }}>
              <MenuItem value="">All</MenuItem>
              {(options?.categories || []).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Priority" value={filters.priority} onChange={setFilter('priority')} sx={{ minWidth: 140 }}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="none">Not set</MenuItem>
              {(options?.priorities || []).map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          </Paper>

          {truncated && <Alert severity="warning" sx={{ mb: 2 }}>Showing the newest 1000 suggestions only.</Alert>}

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Title', 'Category', 'Service', 'Impact', 'Status', 'Priority', 'Owner', 'Target', 'Submitted'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    {ideas.length === 0 ? 'No suggestions registered yet.' : 'No suggestions match the filters.'}
                  </TableCell></TableRow>
                )}
                {visible.map((i) => (
                  <TableRow key={i._id} hover onClick={() => setSelected(i)} sx={{ cursor: 'pointer' }}>
                    <TableCell sx={{ maxWidth: 320, fontWeight: 600 }}>{i.title}</TableCell>
                    <TableCell>{i.category}</TableCell>
                    <TableCell>{i.affectedService}</TableCell>
                    <TableCell>{i.impactType}</TableCell>
                    <TableCell><Chip size="small" label={i.status} color={STATUS_COLOR[i.status] || 'default'} /></TableCell>
                    <TableCell>{i.priority ? <Chip size="small" label={i.priority} color={PRIORITY_COLOR[i.priority]} variant="outlined" /> : '—'}</TableCell>
                    <TableCell>{i.owner || '—'}</TableCell>
                    <TableCell>{i.targetQuarter || '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(i.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {selected && (
        <IdeaDialog
          idea={selected}
          options={options}
          canEdit={canEdit}
          onClose={() => setSelected(null)}
          onSaved={(updated) => { setIdeas((list) => list.map((i) => (i._id === updated._id ? updated : i))); setSelected(null); }}
          onDeleted={(id) => { setIdeas((list) => list.filter((i) => i._id !== id)); setSelected(null); }}
        />
      )}
    </Box>
  );
};

export default WorkstreamIdeasTab;
